import { StatusBadge } from '@/components/features/WorkOrders/components/StatusBadge';
import { TypeBadge } from '@/components/features/WorkOrders/components/TypeBadge';
import { PriorityBadge } from '@/components/global/PriorityBadge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/shadcn/avatar';
import { Card, CardContent, CardHeader } from '@/components/ui/shadcn/card';
import { useTranslations } from '@/hooks/useTranslations';
import { getCachedImageUrl } from '@/lib/image-cache';
import { cn, formatDate } from '@/lib/utils';
import { type WorkOrder } from '@/types';
import { Link } from '@inertiajs/react';
import { differenceInDays } from 'date-fns';
import { Building2, Calendar, ExternalLink, MapPin, User } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

interface WorkOrderCardProps {
    workOrder: WorkOrder;
    statuses?: Array<{ value: string; label: string; color: string; isTerminal?: boolean }>;
    priorities?: Array<{ value: string; label: string; color: string; icon?: string }>;
    types?: Array<{ value: string; label: string; color: string; icon?: string }>;
}

export function WorkOrderCard({ workOrder, statuses, priorities, types }: WorkOrderCardProps) {
    const { t } = useTranslations();
    const contactName = workOrder.full_contact_name || `${workOrder.contact_first_name} ${workOrder.contact_last_name}`;
    const assignedUser = workOrder.assigned_user;

    // Build full address from separate fields (same as table)
    const fullAddress = useMemo(() => {
        const addressParts = [
            workOrder.service_address_street,
            workOrder.service_address_house_number,
            workOrder.service_address_city,
            workOrder.service_address_postal_code,
        ].filter((part): part is string => Boolean(part));
        return addressParts.length > 0 ? addressParts.join(', ') : workOrder.service_address || null;
    }, [
        workOrder.service_address_street,
        workOrder.service_address_house_number,
        workOrder.service_address_city,
        workOrder.service_address_postal_code,
        workOrder.service_address,
    ]);

    const statusKey = (workOrder.status ?? '').toLowerCase();
    const isCompleted = statusKey === 'completed';
    const isCancelled = statusKey === 'canceled' || statusKey === 'cancelled';

    const terminalDate = useMemo(() => {
        const candidate = isCompleted ? workOrder.completed_at : isCancelled ? workOrder.updated_at : null;
        if (!candidate) return null;

        const parsed = new Date(candidate);
        if (Number.isNaN(parsed.getTime())) return null;

        parsed.setHours(0, 0, 0, 0);
        return parsed;
    }, [isCancelled, isCompleted, workOrder.completed_at, workOrder.updated_at]);

    // Calculate days remaining/overdue OR early/late (if terminal)
    const daysInfo = useMemo(() => {
        if (!workOrder.estimated_date) return null;

        const estimatedDate = new Date(workOrder.estimated_date);
        if (Number.isNaN(estimatedDate.getTime())) return null;
        estimatedDate.setHours(0, 0, 0, 0);

        if (terminalDate) {
            const daysDiff = differenceInDays(terminalDate, estimatedDate); // +late, -early
            return {
                mode: 'terminal' as const,
                days: Math.abs(daysDiff),
                isLate: daysDiff > 0,
                isEarly: daysDiff < 0,
                isOnTime: daysDiff === 0,
            };
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const daysDiff = differenceInDays(estimatedDate, today);
        return {
            mode: 'live' as const,
            days: Math.abs(daysDiff),
            isOverdue: daysDiff < 0,
            isDueToday: daysDiff === 0,
        };
    }, [terminalDate, workOrder.estimated_date]);

    // Generate Google Maps static image URL and link
    const mapData = useMemo(() => {
        const env = import.meta.env as unknown as Record<string, string | undefined>;
        const apiKey = env.VITE_GOOGLE_MAPS_API_KEY || env.GOOGLE_MAPS_API_KEY;

        if (!apiKey) return null;

        const hasCoordinates = workOrder.service_address_lat && workOrder.service_address_lng;
        const hasAddress = fullAddress;

        if (!hasCoordinates && !hasAddress) return null;

        let staticMapUrl: string;
        let googleMapsLink: string;

        if (hasCoordinates) {
            const lat = workOrder.service_address_lat;
            const lng = workOrder.service_address_lng;
            staticMapUrl = `https://maps.googleapis.com/maps/api/staticmap?center=${lat},${lng}&zoom=15&size=400x150&markers=color:red|${lat},${lng}&key=${apiKey}`;
            googleMapsLink = `https://www.google.com/maps?q=${lat},${lng}`;
        } else if (hasAddress) {
            const encodedAddress = encodeURIComponent(fullAddress);
            staticMapUrl = `https://maps.googleapis.com/maps/api/staticmap?center=${encodedAddress}&zoom=15&size=400x150&markers=color:red|${encodedAddress}&key=${apiKey}`;
            googleMapsLink = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
        } else {
            return null;
        }

        return { staticMapUrl, googleMapsLink };
    }, [workOrder.service_address_lat, workOrder.service_address_lng, fullAddress]);

    // Cache and load the map image
    const [cachedImageUrl, setCachedImageUrl] = useState<string | null>(null);
    const blobUrlRef = useRef<string | null>(null);

    useEffect(() => {
        if (!mapData?.staticMapUrl) {
            setCachedImageUrl(null);
            return;
        }

        let isMounted = true;

        // Clean up previous blob URL if it exists
        if (blobUrlRef.current && blobUrlRef.current.startsWith('blob:')) {
            URL.revokeObjectURL(blobUrlRef.current);
            blobUrlRef.current = null;
        }

        getCachedImageUrl(mapData.staticMapUrl)
            .then((url) => {
                if (isMounted) {
                    blobUrlRef.current = url.startsWith('blob:') ? url : null;
                    setCachedImageUrl(url);
                }
            })
            .catch((error) => {
                console.error('Error loading cached image:', error);
                if (isMounted) {
                    // Fallback to original URL
                    blobUrlRef.current = null;
                    setCachedImageUrl(mapData.staticMapUrl);
                }
            });

        return () => {
            isMounted = false;
            // Clean up blob URL when component unmounts or URL changes
            if (blobUrlRef.current && blobUrlRef.current.startsWith('blob:')) {
                URL.revokeObjectURL(blobUrlRef.current);
                blobUrlRef.current = null;
            }
        };
    }, [mapData?.staticMapUrl]);

    // Determine if work order is overdue (not terminal status)
    const isOverdue = useMemo(() => {
        if (isCompleted || isCancelled) return false; // Don't show overdue for completed/cancelled
        return daysInfo?.mode === 'live' && daysInfo.isOverdue === true;
    }, [daysInfo, isCompleted, isCancelled]);

    return (
        <Card
            className={cn(
                'h-full cursor-pointer overflow-hidden transition-all hover:shadow-md',
                isOverdue
                    ? 'border-l-destructive/60 border-destructive/10 bg-destructive/5 hover:border-l-destructive/80 hover:bg-destructive/10 border-t border-r border-b border-l-4'
                    : 'border-none',
            )}
        >
            <CardHeader
                className="group relative -mx-6 -mt-6 cursor-pointer p-0 pb-3"
                onClick={(e) => {
                    if (mapData) {
                        e.preventDefault();
                        e.stopPropagation();
                        window.open(mapData.googleMapsLink, '_blank', 'noopener,noreferrer');
                    }
                }}
            >
                {/* Google Maps Snapshot Overlay */}
                {mapData && cachedImageUrl && (
                    <>
                        <div className="absolute inset-0 rounded-t-xl bg-gradient-to-b from-black/20 via-black/10 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
                        <img
                            src={cachedImageUrl}
                            alt="Location map"
                            className="pointer-events-none absolute inset-0 h-full w-full rounded-t-xl object-cover opacity-30 transition-opacity group-hover:opacity-40"
                        />
                        <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center opacity-0 transition-opacity group-hover:opacity-100">
                            <div className="bg-background/90 text-foreground flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs shadow-lg backdrop-blur-sm">
                                <ExternalLink className="h-3.5 w-3.5" />
                                <span>Open in Maps</span>
                            </div>
                        </div>
                    </>
                )}
                <div className="relative z-10 mx-6 flex items-start justify-between px-6 pt-6">
                    <div className="min-w-0 flex-1 space-y-1">
                        <div className="flex items-center gap-2">
                            <span className="text-lg font-semibold">{workOrder.wo_number}</span>
                        </div>
                        <p className="text-muted-foreground line-clamp-2 text-sm">{workOrder.description}</p>
                    </div>
                    <div
                        className="flex flex-shrink-0 flex-col items-end gap-2"
                        onClick={(e) => {
                            e.stopPropagation();
                            window.location.href = `/work-orders/${workOrder.id}`;
                        }}
                    >
                        <StatusBadge status={workOrder.status} statuses={statuses} />
                        <PriorityBadge priority={workOrder.priority} priorities={priorities} />
                        {(workOrder as { work_type?: string }).work_type && (
                            <TypeBadge type={(workOrder as { work_type?: string }).work_type!} types={types} />
                        )}
                    </div>
                </div>
            </CardHeader>
            <Link href={`/work-orders/${workOrder.id}`}>
                <CardContent className="space-y-3">
                    {/* Contact Info */}
                    <div className="flex items-center gap-2 text-sm">
                        <User className="text-muted-foreground h-4 w-4 shrink-0" />
                        <span className="truncate">{contactName}</span>
                    </div>

                    {/* Service Address */}
                    {fullAddress && (
                        <div className="flex items-start gap-2 text-sm">
                            <MapPin className="text-muted-foreground mt-0.5 h-4 w-4 shrink-0" />
                            <span className="text-muted-foreground line-clamp-2">{fullAddress}</span>
                        </div>
                    )}

                    {/* Company */}
                    {workOrder.company && (
                        <div className="flex items-center gap-2 text-sm">
                            <Building2 className="text-muted-foreground h-4 w-4 shrink-0" />
                            <span className="truncate">{workOrder.company.name}</span>
                        </div>
                    )}

                    {/* Assigned User */}
                    {assignedUser && (
                        <div className="flex items-center gap-2">
                            <Avatar className="h-6 w-6">
                                <AvatarImage src={assignedUser.avatar} />
                                <AvatarFallback className="text-xs">
                                    {assignedUser.first_name?.[0]}
                                    {assignedUser.last_name?.[0]}
                                </AvatarFallback>
                            </Avatar>
                            <span className="text-muted-foreground text-sm">
                                {assignedUser.first_name} {assignedUser.last_name}
                            </span>
                        </div>
                    )}

                    {/* Dates */}
                    <div className="text-muted-foreground flex flex-col gap-2 border-t pt-2 text-xs">
                        {workOrder.estimated_date && (
                            <div className="flex flex-col gap-0.5">
                                <div className="flex items-center gap-1">
                                    <Calendar className="h-3 w-3" />
                                    <span>
                                        {t('work_orders.date_status.estimated', 'Est:')} {formatDate(workOrder.estimated_date)}
                                    </span>
                                </div>
                            </div>
                        )}
                        {workOrder.completed_at && (
                            <div className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                <span>
                                    {t('work_orders.date_status.completed', 'Completed:')} {formatDate(workOrder.completed_at)}
                                    {daysInfo && (
                                        <span
                                            className={
                                                daysInfo.mode === 'terminal'
                                                    ? daysInfo.isLate
                                                        ? 'text-destructive ml-4 font-medium'
                                                        : daysInfo.isEarly
                                                          ? 'text-success-subtle-foreground ml-4 font-medium'
                                                          : 'text-muted-foreground ml-4'
                                                    : daysInfo.isOverdue
                                                      ? 'text-destructive ml-4 font-medium'
                                                      : 'text-muted-foreground ml-4'
                                            }
                                        >
                                            (
                                            {daysInfo.mode === 'terminal'
                                                ? daysInfo.isLate
                                                    ? `${daysInfo.days} ${daysInfo.days === 1 ? t('work_orders.date_status.day', 'day') : t('work_orders.date_status.days', 'days')} ${t('work_orders.date_status.late', 'late')}`
                                                    : daysInfo.isEarly
                                                      ? `${daysInfo.days} ${daysInfo.days === 1 ? t('work_orders.date_status.day', 'day') : t('work_orders.date_status.days', 'days')} ${t('work_orders.date_status.early', 'early')}`
                                                      : t('work_orders.date_status.on_time', 'On time')
                                                : daysInfo.isOverdue
                                                  ? `${daysInfo.days} ${daysInfo.days === 1 ? t('work_orders.date_status.day', 'day') : t('work_orders.date_status.days', 'days')} ${t('work_orders.index.filters.overdue', 'overdue')}`
                                                  : daysInfo.isDueToday
                                                    ? t('work_orders.date_status.due_today', 'Due today')
                                                    : `${daysInfo.days} ${daysInfo.days === 1 ? t('work_orders.date_status.day', 'day') : t('work_orders.date_status.days', 'days')} ${t('work_orders.date_status.left', 'left')}`}
                                            )
                                        </span>
                                    )}
                                </span>
                            </div>
                        )}
                    </div>
                </CardContent>
            </Link>
        </Card>
    );
}
